/**
 * Rules-only redaction of account, card and routing numbers in statement text.
 * No model is involved: these are regular expressions plus a stable token per
 * distinct number, so the same card is "[CARD-A]" on every page and statement
 * the user processes in one sitting. Runs in the browser before upload and
 * again in the API as a fail-closed check.
 */

export type SensitiveKind = "card" | "account" | "routing" | "number";

export interface SensitiveMatch {
  kind: SensitiveKind;
  /** The matched text exactly as it appeared. */
  text: string;
  start: number;
  end: number;
}

interface Rule {
  kind: SensitiveKind;
  re: RegExp;
}

// Order matters: labelled, specific rules first; overlapping later matches are dropped.
const RULES: Rule[] = [
  // Routing numbers are 9 digits, usually labelled.
  { kind: "routing", re: /\b(?:routing|aba|rtn)[^\d\n]{0,20}\d{9}\b/gi },
  // Account numbers: labelled runs of 6+ digits, possibly hyphenated.
  { kind: "account", re: /\b(?:account|acct\.?|a\/c)(?: number| no\.?| #)?[:\s#]*\d(?:[\d-]{5,20})\d\b/gi },
  // Full 15/16-digit card numbers, with or without separators.
  { kind: "card", re: /\b\d(?:[ -]?\d){14,15}\b/g },
  // Masked card forms: "XXXX XXXX XXXX 6070", "**** 6070", "ending in 6070", "x6070".
  { kind: "card", re: /(?:[xX*]{4}[ -]?){1,3}\d{4}\b/g },
  { kind: "card", re: /\b(?:ending(?: in)?|last four(?: digits)?|card(?: number)?(?: ending)?)[:\s#]*\d{4}\b/gi },
  { kind: "card", re: /\b[xX]{1,2}\d{4}\b/g },
  // Any remaining run of 8 or more digits (allowing single spaces or hyphens between groups).
  { kind: "number", re: /\b\d(?:[ -]?\d){7,}\b/g },
];

// Things that look like long digit runs but are not secrets.
const ALLOWLIST: RegExp[] = [
  // Phone numbers: on merchant lines (855-431-0459) and in issuer contact boxes (1-800-432-3117).
  /^1?[ -]?\d{3}[ -]\d{3}[ -]\d{4}$/,
  // ZIP+4 in mailing addresses: 19850-5298.
  /^\d{5}-\d{4}$/,
  /^(?:19|20)\d{2}[ -]?\d{2}[ -]?\d{2}$/, // ISO-ish dates without separators
];

export function findSensitive(text: string): SensitiveMatch[] {
  const found: SensitiveMatch[] = [];
  const taken: Array<[number, number]> = [];
  const overlaps = (s: number, e: number) => taken.some(([ts, te]) => s < te && e > ts);
  for (const rule of RULES) {
    rule.re.lastIndex = 0;
    for (const m of text.matchAll(rule.re)) {
      const start = m.index ?? 0;
      const end = start + m[0].length;
      if (overlaps(start, end)) continue;
      if (ALLOWLIST.some((a) => a.test(m[0].trim()))) continue;
      taken.push([start, end]);
      found.push({ kind: rule.kind, text: m[0], start, end });
    }
  }
  return found.sort((a, b) => a.start - b.start);
}

const PREFIX: Record<SensitiveKind, string> = {
  card: "CARD",
  account: "ACCT",
  routing: "ROUTING",
  number: "NUM",
};

function letters(n: number): string {
  // 0 -> A, 25 -> Z, 26 -> AA ...
  let s = "";
  n += 1;
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

/** Only the digits matter for identity, so "XXXX 6070" and "x6070" share a token. */
function identity(m: SensitiveMatch): string {
  const digits = m.text.replace(/\D/g, "");
  return `${m.kind}:${m.kind === "card" ? digits.slice(-4) : digits}`;
}

export interface RedactionResult {
  text: string;
  /** token -> kind, e.g. { "[CARD-A]": "card" } */
  tokens: Record<string, SensitiveKind>;
  matches: SensitiveMatch[];
}

/**
 * Replace every sensitive number with a stable token. `tokenMap` carries tokens
 * across calls (pages, files) so the same account keeps the same token.
 */
export function redactText(text: string, tokenMap: Map<string, string> = new Map()): RedactionResult {
  const matches = findSensitive(text);
  const tokens: Record<string, SensitiveKind> = {};
  let out = "";
  let cursor = 0;
  for (const m of matches) {
    const id = identity(m);
    let token = tokenMap.get(id);
    if (!token) {
      const count = [...tokenMap.keys()].filter((k) => k.startsWith(`${m.kind}:`)).length;
      token = `[${PREFIX[m.kind]}-${letters(count)}]`;
      tokenMap.set(id, token);
    }
    tokens[token] = m.kind;
    out += text.slice(cursor, m.start) + token;
    cursor = m.end;
  }
  out += text.slice(cursor);
  return { text: out, tokens, matches };
}

/** True when nothing sensitive remains. Used as the fail-closed gate before upload and on the API. */
export function isClean(text: string): boolean {
  return findSensitive(text).length === 0;
}
